import React, { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LogOut, Inbox, Filter, Mail, Phone, User, Home, CheckCircle2, Search, Bell, Building2, ShieldCheck, AlertTriangle, ExternalLink, ClipboardCopy, Upload, X, Zap, RefreshCw } from "lucide-react";
import { adminApi, clearAdminToken, getAdminToken, setAdminToken, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { PROPERTIES } from "../data/properties";
import { statusBadge } from "../data/properties";
import { useProperties, invalidateProperties } from "../lib/useProperties";

function LoginView() {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { password: pwd });
      setAdminToken(data.access_token);
      toast.success("Welcome back.");
      window.location.replace("/admin/inbox");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4" data-testid="admin-login">
      <form onSubmit={submit} className="alpha-card p-8 w-full max-w-md">
        <img src="/brand/logo-green-mark.png" alt="Alpha Community Care" className="h-12 w-auto" />
        <h1 className="mt-5 font-display text-2xl font-semibold">Admin sign-in</h1>
        <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Submissions inbox for the Alpha Community Care team.</p>
        <label className="text-sm font-semibold mt-6 block">Password</label>
        <Input type="password" className="mt-2 h-11" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="admin-password" />
        <button disabled={loading || !pwd} className="alpha-btn-primary mt-5 w-full justify-center" data-testid="admin-login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="mt-4 block text-center text-xs text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">← Back to site</Link>
      </form>
    </div>
  );
}

const KIND_LABEL = {
  contact: "Contact",
  participant_referral: "Participant referral",
  coordinator_referral: "Coordinator referral",
  vacancy_enquiry: "Vacancy enquiry",
  vacancy_alert: "Vacancy alert",
};
const KIND_ICON = {
  contact: Mail,
  participant_referral: User,
  coordinator_referral: User,
  vacancy_enquiry: Home,
  vacancy_alert: Bell,
};

function InboxView() {
  const [data, setData] = useState({ items: [], totals: {}, unread: {} });
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== "all") params.kind = kind;
      if (status !== "all") params.status = status;
      const { data } = await adminApi.get("/admin/submissions", { params });
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        nav("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, status]);

  const logout = () => {
    clearAdminToken();
    nav("/admin");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await adminApi.patch(`/admin/submissions/${id}`, { status: newStatus });
      toast.success("Updated");
      setActive((a) => (a && a.id === id ? { ...a, status: newStatus } : a));
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (kind !== "all") params.set("kind", kind);
      if (status !== "all") params.set("status", status);
      const res = await adminApi.get(`/admin/submissions.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `alpha-submissions-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = data.items.filter((s) => {
    if (!q) return true;
    const blob = JSON.stringify(s).toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  const totalUnread = Object.values(data.unread).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-[color:var(--alpha-soft)]" data-testid="admin-inbox">
      <div className="bg-white border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-green-mark.png" alt="Alpha" className="h-9 w-auto" />
            <div>
              <div className="font-display font-semibold">Admin · Submissions inbox</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[color:var(--alpha-mute)]">{totalUnread} unread</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} className="alpha-btn-ghost text-sm !py-2 !px-4" data-testid="admin-export-csv">
              Export CSV
            </button>
            <Link to="/admin/properties" className="alpha-btn-ghost text-sm !py-2 !px-4" data-testid="admin-nav-properties"><Building2 className="w-4 h-4" /> Properties</Link>
            <Link to="/" className="alpha-btn-ghost text-sm">View site</Link>
            <button onClick={logout} className="text-sm font-semibold text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)] flex items-center gap-1"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="alpha-container py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 xl:col-span-3 space-y-4">
          <div className="alpha-card p-5">
            <div className="alpha-overline flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Filter</div>
            <div className="mt-4 space-y-3">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-10" data-testid="admin-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="participant_referral">Participant referral</SelectItem>
                  <SelectItem value="coordinator_referral">Coordinator referral</SelectItem>
                  <SelectItem value="vacancy_enquiry">Vacancy enquiry</SelectItem>
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-10" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--alpha-mute)]" />
                <Input className="pl-9 h-10" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email…" data-testid="admin-search" />
              </div>
            </div>
          </div>
          <div className="alpha-card p-5">
            <div className="alpha-overline">Overview</div>
            <ul className="mt-3 space-y-2 text-sm">
              {Object.entries(KIND_LABEL).map(([k, label]) => (
                <li key={k} className="flex items-center justify-between">
                  <span>{label}</span>
                  <span className="alpha-chip">{data.totals[k] || 0}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="lg:col-span-8 xl:col-span-9">
          <div className="alpha-card p-0 overflow-hidden">
            <div className="p-5 border-b border-[color:var(--alpha-line)] flex items-center gap-2">
              <Inbox className="w-4 h-4" /> <span className="font-display font-semibold">Submissions</span>
              <span className="ml-auto text-xs text-[color:var(--alpha-mute)]">{loading ? "Loading…" : `${filtered.length} items`}</span>
            </div>
            <ul className="divide-y divide-[color:var(--alpha-line)] max-h-[70vh] overflow-y-auto">
              {filtered.length === 0 && !loading && (
                <li className="p-10 text-center text-sm text-[color:var(--alpha-mute)]">No submissions match.</li>
              )}
              {filtered.map((s) => {
                const Icon = KIND_ICON[s.kind] || Mail;
                const who = s.data.full_name || s.data.name || s.data.coordinator_name || "Unknown";
                const email = s.data.email;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setActive(s)}
                      className={`w-full text-left p-4 hover:bg-[color:var(--alpha-soft)] transition-colors flex items-start gap-3 ${active?.id === s.id ? "bg-[#EAF4FB]" : ""}`}
                      data-testid={`admin-row-${s.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.status === "new" ? "alpha-gradient text-white" : "bg-[color:var(--alpha-soft)] text-[color:var(--alpha-purple-700)]"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold truncate">{who}</div>
                          {s.status === "new" && <span className="alpha-chip !bg-[#E6F4EC] !text-[#3F8F5D] !border-[#CCE5D6]">New</span>}
                          {s.status === "contacted" && <span className="alpha-chip !bg-[color:var(--alpha-purple-100)] !text-[color:var(--alpha-purple-700)]">Contacted</span>}
                          {s.status === "converted" && <span className="alpha-chip !bg-[#DCEEE2] !text-[color:var(--alpha-green-700)] !border-[#CCE5D6]">Converted</span>}
                        </div>
                        <div className="text-xs text-[color:var(--alpha-mute)] truncate">{KIND_LABEL[s.kind]} · {email || "no email"}</div>
                      </div>
                      <div className="text-[11px] text-[color:var(--alpha-mute)] whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {active && (
            <div className="alpha-card p-6 mt-5" data-testid="admin-active-detail">
              <div className="flex items-center justify-between">
                <div>
                  <div className="alpha-overline">{KIND_LABEL[active.kind]}</div>
                  <div className="font-display text-lg font-semibold mt-1">{active.data.full_name || active.data.name || active.data.coordinator_name}</div>
                  <div className="text-xs text-[color:var(--alpha-mute)] mt-1">Received {new Date(active.created_at).toLocaleString()} · ID {active.id.slice(0, 8)}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {active.status !== "contacted" && <button onClick={() => updateStatus(active.id, "contacted")} className="alpha-btn-ghost text-xs py-2 px-3" data-testid="admin-mark-contacted">Mark contacted</button>}
                  {active.status !== "read" && <button onClick={() => updateStatus(active.id, "read")} className="alpha-btn-ghost text-xs py-2 px-3" data-testid="admin-mark-read">Mark read</button>}
                  {active.status !== "converted" && <button onClick={() => updateStatus(active.id, "converted")} className="alpha-btn-green text-xs py-2 px-3" data-testid="admin-mark-converted"><CheckCircle2 className="w-3.5 h-3.5" /> Converted</button>}
                  {active.status !== "resolved" && <button onClick={() => updateStatus(active.id, "resolved")} className="alpha-btn-primary text-xs py-2 px-3" data-testid="admin-mark-resolved"><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>}
                </div>
              </div>
              {(active.data.email || active.data.phone) && (
                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  {active.data.email && <a href={`mailto:${active.data.email}`} className="flex items-center gap-2 text-[color:var(--alpha-purple-700)] font-semibold"><Mail className="w-4 h-4" /> {active.data.email}</a>}
                  {active.data.phone && <a href={`tel:${active.data.phone}`} className="flex items-center gap-2 text-[color:var(--alpha-purple-700)] font-semibold"><Phone className="w-4 h-4" /> {active.data.phone}</a>}
                </div>
              )}
              <table className="mt-5 w-full text-sm">
                <tbody>
                  {Object.entries(active.data).map(([k, v]) => (
                    <tr key={k} className="border-b border-[color:var(--alpha-line)]/70">
                      <td className="py-2 pr-4 font-semibold capitalize text-[color:var(--alpha-mute)] w-1/3">{k.replace(/_/g, " ")}</td>
                      <td className="py-2">{Array.isArray(v) ? v.join(", ") : v ? String(v) : <span className="text-[color:var(--alpha-mute)]">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function PropertiesView() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [savingSlug, setSavingSlug] = useState(null);
  const [digestRunning, setDigestRunning] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const nav = useNavigate();
  const logout = () => { clearAdminToken(); nav("/admin"); };
  const { properties: LIVE_PROPERTIES, refresh } = useProperties();

  const rows = LIVE_PROPERTIES.filter((p) => {
    if (filter === "verified" && p.recordVerificationStatus !== "verified") return false;
    if (filter === "demo" && p.recordVerificationStatus !== "demonstration") return false;
    if (filter === "awaiting" && p.recordVerificationStatus !== "awaitingVerification") return false;
    if (q) {
      const blob = `${p.name} ${p.suburb} ${p.state} ${p.slug}`.toLowerCase();
      if (!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const counts = {
    verified: LIVE_PROPERTIES.filter((p) => p.recordVerificationStatus === "verified").length,
    demo: LIVE_PROPERTIES.filter((p) => p.recordVerificationStatus === "demonstration").length,
    awaiting: LIVE_PROPERTIES.filter((p) => p.recordVerificationStatus === "awaitingVerification").length,
  };

  const copyChecklist = (p) => {
    const checklist = [
      `Property: ${p.name}`,
      `Slug: ${p.slug}`,
      `Location: ${p.suburb}, ${p.state}`,
      `Support: ${(p.support_ratios || []).join(", ")} · ${(p.support_model || []).join(", ")}`,
      "",
      "VERIFICATION CHECKLIST",
      "[ ] Real property photos received (hero + gallery)",
      "[ ] Property owner / SDA partner confirmed",
      "[ ] Vacancy count confirmed",
      "[ ] Available-from date confirmed",
      "[ ] Accessibility features verified on-site",
      "[ ] Suitability tags reviewed by intake",
      "[ ] Support ratios agreed with roster team",
      "[ ] Internal.contact populated",
    ].join("\n");
    navigator.clipboard.writeText(checklist).then(() => toast.success("Checklist copied to clipboard"));
  };

  const confirmVerify = async () => {
    if (!verifyTarget) return;
    setSavingSlug(verifyTarget.slug);
    try {
      await adminApi.put(`/admin/properties/${verifyTarget.slug}`, {
        recordVerificationStatus: "verified",
        vacancy_last_updated: new Date().toISOString().slice(0, 10),
      });
      toast.success(`${verifyTarget.name} is now verified and live.`);
      invalidateProperties(); refresh();
      setVerifyTarget(null);
    } catch (e) {
      toast.error("Couldn't promote to verified — please try again.");
    } finally {
      setSavingSlug(null);
    }
  };

  const uploadPhoto = async (slug, file) => {
    setSavingSlug(slug);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("alt", `${slug.replace(/-/g, " ")} property photo`);
      form.append("replace_hero", "true");
      await adminApi.post(`/admin/properties/${slug}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded — hero image updated site-wide.");
      invalidateProperties(); refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Photo upload failed.";
      toast.error(msg);
    } finally {
      setSavingSlug(null);
    }
  };

  const runDigest = async () => {
    setDigestRunning(true);
    try {
      const verifiedProps = LIVE_PROPERTIES.filter((p) => p.recordVerificationStatus === "verified");
      const { data } = await adminApi.post("/admin/alerts/run-digest", { properties: verifiedProps });
      setDigestResult(data);
      toast.success(`Digest run: ${data.matched_alerts}/${data.total_alerts} alerts matched.`);
    } catch (e) {
      toast.error("Digest run failed.");
    } finally {
      setDigestRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--alpha-soft)]" data-testid="admin-properties">
      <div className="bg-white border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-green-mark.png" alt="Alpha" className="h-9 w-auto" />
            <div>
              <div className="font-display font-semibold">Admin · Properties</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[color:var(--alpha-mute)]">{counts.verified} verified · {counts.demo} demo · {counts.awaiting} awaiting</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/inbox" className="alpha-btn-ghost text-sm !py-2 !px-4" data-testid="admin-nav-inbox"><Inbox className="w-4 h-4" /> Inbox</Link>
            <Link to="/" className="alpha-btn-ghost text-sm">View site</Link>
            <button onClick={logout} className="text-sm font-semibold text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)] flex items-center gap-1"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="alpha-container py-8 space-y-6">
        <div className="alpha-card p-6 border-l-4 border-l-amber-500">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-lg font-semibold">Verified property onboarding — now one click</h2>
              <p className="mt-2 text-sm text-[color:var(--alpha-mute)] leading-relaxed">
                Demonstration properties are automatically hidden on the production hostname. Use the workflow below to bring a home live.
              </p>
              <ol className="mt-3 text-sm text-[color:var(--alpha-mute)] space-y-1 list-decimal list-inside">
                <li>Drop the real property photo onto the demo card — hero image updates site-wide within seconds</li>
                <li>Complete the verification checklist (copy it from the card)</li>
                <li>Click <strong>Promote to Verified</strong> — a confirmation dialog protects against accidental publishing</li>
                <li>The listing appears on the production site instantly (no code deploy)</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="alpha-card p-5 border-l-4 border-l-[color:var(--alpha-purple-700)]">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[color:var(--alpha-purple-700)]" />
                <h3 className="font-display text-base font-semibold">Alert digest runner</h3>
              </div>
              <p className="mt-1 text-sm text-[color:var(--alpha-mute)] max-w-2xl">Match current verified inventory against every active vacancy-alert subscription. Results are logged to the Inbox as a <code>digest_run</code> record.</p>
              {digestResult && (
                <div className="mt-2 text-xs text-[color:var(--alpha-mute)]" data-testid="digest-result">
                  Last run: <strong className="text-[color:var(--alpha-ink)]">{digestResult.matched_alerts}</strong> of <strong className="text-[color:var(--alpha-ink)]">{digestResult.total_alerts}</strong> alerts matched inventory.
                </div>
              )}
            </div>
            <button onClick={runDigest} disabled={digestRunning} className="alpha-btn-primary text-sm" data-testid="admin-run-digest">
              {digestRunning ? <><RefreshCw className="w-4 h-4 animate-spin" /> Running…</> : <><Zap className="w-4 h-4" /> Run digest now</>}
            </button>
          </div>
        </div>

        <div className="alpha-card p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--alpha-mute)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, suburb, slug…" className="pl-9 h-10" data-testid="admin-properties-search" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "all", l: `All (${LIVE_PROPERTIES.length})` },
              { v: "verified", l: `Verified (${counts.verified})` },
              { v: "demo", l: `Demo (${counts.demo})` },
              { v: "awaiting", l: `Awaiting (${counts.awaiting})` },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setFilter(t.v)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === t.v ? "bg-[color:var(--alpha-purple-700)] text-white border-[color:var(--alpha-purple-800)]" : "bg-white text-[color:var(--alpha-ink)] border-[color:var(--alpha-line)] hover:border-[color:var(--alpha-purple-700)]"}`}
                data-testid={`admin-properties-filter-${t.v}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {rows.map((p) => {
            const sb = statusBadge(p);
            const isDemo = p.recordVerificationStatus === "demonstration";
            const isAwait = p.recordVerificationStatus === "awaitingVerification";
            return (
              <div key={p.slug} className="alpha-card p-5" data-testid={`admin-prop-${p.slug}`}>
                {p.hero_image && (
                  <div className="mb-3 -mx-5 -mt-5 h-32 overflow-hidden bg-[color:var(--alpha-soft)]">
                    <img src={p.hero_image.startsWith("http") ? p.hero_image : `${API}${p.hero_image}`} alt={`${p.name} hero`} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[color:var(--alpha-mute)]">{p.suburb}, {p.state}</div>
                    <h3 className="font-display font-semibold text-base mt-0.5 truncate">{p.name}</h3>
                  </div>
                  {p.recordVerificationStatus === "verified" ? (
                    <span className="alpha-chip !text-[10px] !py-0.5 !px-2 !bg-[color:var(--alpha-green-50)] !text-[color:var(--alpha-green-700)] !border-[color:var(--alpha-green-100)] inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
                  ) : isDemo ? (
                    <span className="alpha-chip !text-[10px] !py-0.5 !px-2 !bg-black/80 !text-white !border-transparent">Demo</span>
                  ) : (
                    <span className="alpha-chip !text-[10px] !py-0.5 !px-2 !bg-amber-100 !text-amber-800 !border-amber-200">Awaiting</span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                  <span className="alpha-chip !text-[10.5px] !py-0.5 !px-2 alpha-chip-neutral">{p.sda_category ? "SDA" : "SIL"}</span>
                  <span className={`alpha-chip !text-[10.5px] !py-0.5 !px-2 ${sb.tone === "green" ? "!bg-[color:var(--alpha-green-50)] !text-[color:var(--alpha-green-700)] !border-[color:var(--alpha-green-100)]" : "alpha-chip-neutral"}`}>{sb.label}</span>
                  <span className="alpha-chip !text-[10.5px] !py-0.5 !px-2 alpha-chip-neutral">{p.vacancies || 0} vacancies</span>
                </div>

                {/* Photo uploader — drag & drop OR click */}
                <label
                  htmlFor={`photo-upload-${p.slug}`}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("!border-[color:var(--alpha-purple-700)]", "!bg-[color:var(--alpha-purple-50)]"); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("!border-[color:var(--alpha-purple-700)]", "!bg-[color:var(--alpha-purple-50)]")}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("!border-[color:var(--alpha-purple-700)]", "!bg-[color:var(--alpha-purple-50)]");
                    const f = e.dataTransfer.files?.[0];
                    if (f) uploadPhoto(p.slug, f);
                  }}
                  className="mt-4 block border-2 border-dashed border-[color:var(--alpha-line)] rounded-lg p-3 text-center cursor-pointer hover:border-[color:var(--alpha-purple-700)] transition-colors"
                  data-testid={`admin-prop-${p.slug}-dropzone`}
                >
                  <Upload className="w-4 h-4 inline text-[color:var(--alpha-mute)]" />
                  <span className="ml-2 text-[11px] text-[color:var(--alpha-mute)]">
                    {savingSlug === p.slug ? "Uploading…" : "Drop or click to upload hero photo"}
                  </span>
                  <input
                    id={`photo-upload-${p.slug}`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(p.slug, f); e.target.value = ""; }}
                    data-testid={`admin-prop-${p.slug}-file`}
                  />
                </label>

                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={p.href || `/homes/${p.slug}`} target="_blank" rel="noreferrer" className="alpha-btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`admin-prop-${p.slug}-view`}>
                    <ExternalLink className="w-3 h-3" /> View
                  </a>
                  {(isDemo || isAwait) && (
                    <>
                      <button
                        onClick={() => setVerifyTarget(p)}
                        disabled={savingSlug === p.slug}
                        className="alpha-btn-primary !py-1.5 !px-3 !text-xs"
                        data-testid={`admin-prop-${p.slug}-promote`}
                      >
                        <ShieldCheck className="w-3 h-3" /> Promote to Verified
                      </button>
                      <button onClick={() => copyChecklist(p)} className="alpha-btn-green !py-1.5 !px-3 !text-xs" data-testid={`admin-prop-${p.slug}-checklist`}>
                        <ClipboardCopy className="w-3 h-3" /> Checklist
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {rows.length === 0 && (
          <div className="alpha-card p-10 text-center">
            <div className="font-display text-lg font-semibold">No properties match.</div>
            <button onClick={() => { setQ(""); setFilter("all"); }} className="mt-3 alpha-btn-ghost text-sm">Clear filters</button>
          </div>
        )}
      </div>

      {/* Promote-to-Verified confirmation dialog */}
      <Dialog open={!!verifyTarget} onOpenChange={(o) => !o && setVerifyTarget(null)}>
        <DialogContent data-testid="admin-verify-dialog">
          <DialogHeader>
            <DialogTitle>Promote to verified?</DialogTitle>
          </DialogHeader>
          {verifyTarget && (
            <div className="text-sm text-[color:var(--alpha-mute)] space-y-3">
              <p>
                You're about to publish <strong className="text-[color:var(--alpha-ink)]">{verifyTarget.name}</strong> ({verifyTarget.suburb}, {verifyTarget.state}) to the <strong>live production site</strong>.
              </p>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs">
                <strong>Before you continue, confirm:</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>Real property photos have been uploaded (not stock)</li>
                  <li>Vacancy count is accurate today</li>
                  <li>Accessibility features have been verified on-site</li>
                  <li>Intake lead has approved this property</li>
                </ul>
              </div>
              <p className="text-xs">This action is reversible — you can move the property back to demo/awaiting later.</p>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setVerifyTarget(null)} className="alpha-btn-ghost text-sm">Cancel</button>
            <button onClick={confirmVerify} disabled={!!savingSlug} className="alpha-btn-primary text-sm" data-testid="admin-verify-confirm">
              {savingSlug ? "Publishing…" : <><ShieldCheck className="w-4 h-4" /> Yes, publish live</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminRoutes() {
  const authed = !!getAdminToken();
  return (
    <Routes>
      <Route index element={authed ? <Navigate to="/admin/inbox" replace /> : <LoginView />} />
      <Route path="inbox" element={authed ? <InboxView /> : <Navigate to="/admin" replace />} />
      <Route path="properties" element={authed ? <PropertiesView /> : <Navigate to="/admin" replace />} />
    </Routes>
  );
}
